import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useLocationStore } from '../../../stores/locationStore';
import { Icon } from '../../../pages/DesignSystem/primitives/Icon';
import '../../../styles/components/location-fallback-toast.css';

const SHOW_DELAY_MS = 1500;
const AUTO_DISMISS_MS = 10000;

const LocationFallbackToast: React.FC = () => {
  const error = useLocationStore(state => state.error);
  const requestGeoLocation = useLocationStore(state => state.requestGeoLocation);

  const [phase, setPhase] = useState<'hidden' | 'visible' | 'hiding'>('hidden');
  const [dismissed, setDismissed] = useState(false);
  const autoDismissRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const showDelayRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Show when geoloc denied OR timeout, and not dismissed this session
  const shouldShow = (error === 'geolocation_denied' || error === 'geolocation_timeout') && !dismissed;
  const isTimeout = error === 'geolocation_timeout';

  // Show after delay
  useEffect(() => {
    if (!shouldShow) return;

    showDelayRef.current = setTimeout(() => {
      setPhase('visible');
    }, SHOW_DELAY_MS);

    return () => {
      if (showDelayRef.current) clearTimeout(showDelayRef.current);
    };
  }, [shouldShow]);

  // Auto-dismiss
  useEffect(() => {
    if (phase !== 'visible') return;

    autoDismissRef.current = setTimeout(() => {
      setPhase('hiding');
      setTimeout(() => {
        setDismissed(true);
        setPhase('hidden');
      }, 300);
    }, AUTO_DISMISS_MS);

    return () => {
      if (autoDismissRef.current) clearTimeout(autoDismissRef.current);
    };
  }, [phase]);

  const handleDismiss = useCallback(() => {
    if (autoDismissRef.current) clearTimeout(autoDismissRef.current);
    setPhase('hiding');
    setTimeout(() => {
      setDismissed(true);
      setPhase('hidden');
    }, 300);
  }, []);

  const handleLocateMe = useCallback(async () => {
    if (autoDismissRef.current) clearTimeout(autoDismissRef.current);
    try {
      await requestGeoLocation();
    } catch {
      // Error handled by locationStore
    }
    handleDismiss();
  }, [requestGeoLocation, handleDismiss]);

  const handleChooseLocation = useCallback(() => {
    window.dispatchEvent(new CustomEvent('openLocationFilter'));
    handleDismiss();
  }, [handleDismiss]);

  if (phase === 'hidden') return null;

  const className = [
    'location-fallback-toast',
    phase === 'visible' && 'location-fallback-toast--visible',
    phase === 'hiding' && 'location-fallback-toast--hiding',
  ].filter(Boolean).join(' ');

  return (
    <div className={className} role="status" aria-live="polite">
      <div className="location-fallback-toast__monkey">
        <img
          src="/singequimarcheorange.png"
          alt=""
          width={40}
          height={40}
          loading="eager"
        />
      </div>
      <div className="location-fallback-toast__content">
        <p className="location-fallback-toast__message">
          {isTimeout
            ? <>Location is taking a while — using your <strong>last known position</strong> for now. You can update it anytime!</>
            : <>We couldn't get your location! Our little monkeys are searching around{' '}
              <strong>Hiriketiya</strong> for now — tell them where you are so they
              can find the best events near you!</>
          }
        </p>
        <div className="location-fallback-toast__actions">
          <button
            className="location-fallback-toast__action"
            onClick={handleLocateMe}
            type="button"
          >
            <Icon name="MapPin" size={14} />
            Locate me
          </button>
          <button
            className="location-fallback-toast__action location-fallback-toast__action--secondary"
            onClick={handleChooseLocation}
            type="button"
          >
            <Icon name="Search" size={14} />
            Choose location
          </button>
        </div>
      </div>
      <button
        className="location-fallback-toast__close"
        onClick={handleDismiss}
        aria-label="Close"
        type="button"
      >
        <Icon name="X" size={14} />
      </button>
    </div>
  );
};

export default LocationFallbackToast;
